import { useState } from 'react'
import { confirmDraft, errorMessage } from '../../../services/admin'
import { dictionaryRefusal, linksToWire, pruneWordLinks } from '../dictionary'
import { WordLinkInputs } from './WordLinkInputs'

/**
 * The reviewed, final sentence — the pipeline now yields one per lesson
 * (one exercise = one sentence, 2026-10-13), but a draft made before that
 * may still hold several rows; each can be edited or dropped. The words can
 * be linked to the dictionary right here, before the draft becomes a real
 * sentence. Nothing is translated: teaching is monolingual.
 */
export function DraftReviewForm({ lesson, onConfirmed }) {
  const [rows, setRows] = useState(
    lesson.draftSentences.map((d, i) => ({
      draftIndex: i,
      textEn: d.textEn,
      audioUrl: d.audioUrl,
      audioDurationSeconds: d.audioDurationSeconds,
      wordLinks: [],
    })),
  )
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const setRow = (i, k, v) => setRows(rs => { const c = [...rs]; c[i] = { ...c[i], [k]: v }; return c })
  const setRowText = (i, textEn) =>
    setRows(rs => { const c = [...rs]; c[i] = { ...c[i], textEn, wordLinks: pruneWordLinks(c[i].wordLinks, textEn) }; return c })
  const removeRow = (i) => setRows(rs => rs.filter((_, j) => j !== i))

  async function confirm(e) {
    e.preventDefault()
    if (!rows.length) { setErr('حداقل یک جمله باید باقی بماند.'); return }
    setBusy(true); setErr('')
    try {
      const sentences = rows.map(r => ({
        draftIndex: r.draftIndex,
        textEn: r.textEn,
        wordLinks: linksToWire(r.wordLinks),
      }))
      onConfirmed(await confirmDraft(lesson.id, sentences))
    } catch (ex) { setErr(dictionaryRefusal(ex) ?? errorMessage(ex)) }
    finally { setBusy(false) }
  }

  const FI = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none'

  return (
    <form onSubmit={confirm} className="space-y-3 rounded-xl border border-amber-200 bg-amber-50/40 p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <span className="font-semibold text-slate-800" dir="ltr">{lesson.titleEn}</span>
          {lesson.titleFa && <span className="ms-1.5 text-xs text-slate-400">/ {lesson.titleFa}</span>}
        </div>
        <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
          {rows.length === 1 ? 'نیازمند بازبینی — متن تشخیص‌داده‌شده را بررسی کنید' : `نیازمند بازبینی — ${rows.length} جمله شناسایی‌شده`}
        </span>
      </div>

      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={r.draftIndex} className="space-y-1.5 rounded-lg border border-slate-200 bg-white p-2.5">
            <div className="flex items-center gap-2">
              <span className="w-5 shrink-0 text-center text-xs text-slate-400">{i + 1}</span>
              <audio controls src={r.audioUrl} className="h-8 flex-1" />
              <span className="shrink-0 text-xs text-slate-400">{r.audioDurationSeconds}s</span>
              <button type="button" onClick={() => removeRow(i)} title="حذف این جمله از پیش‌نویس"
                className="shrink-0 text-slate-300 hover:text-red-500">✕</button>
            </div>
            <div className="sm:ps-7">
              <textarea value={r.textEn} rows={2} placeholder="جمله انگلیسی" dir="ltr" className={`${FI} resize-none`}
                onChange={e => setRowText(i, e.target.value)} />
            </div>
            <div className="ps-7">
              <WordLinkInputs textEn={r.textEn} links={r.wordLinks} onChange={next => setRow(i, 'wordLinks', next)} />
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <p className="rounded-lg border border-dashed border-slate-200 py-6 text-center text-xs text-slate-400">
            همهٔ جمله‌ها حذف شدند — حداقل یکی لازم است.
          </p>
        )}
      </div>

      {err && <p className="text-sm text-red-600">{err}</p>}

      <button type="submit" disabled={busy || !rows.length}
        className="rounded-lg bg-brand-600 px-5 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50">
        {busy ? 'در حال ذخیره…' : 'تأیید و ذخیره'}
      </button>
    </form>
  )
}
